import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { PopulateDropdownService } from '../services/populate-dropdown.service';
import { NetworkStatusService } from '../services/network-status.service';
import { LocationSelectionService } from '../modules/commons/location-selection/service/location-selection.service';
import { offlineDb, scopedKey } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { isProtected } from './delta-merge.util';

// Full-list refresh cache for the TU-form dropdown data (buyers, suppliers, products,
// loading sites, transport modes, fumigation types) — these are small, bounded lists,
// so a full re-fetch-and-replace on every online call is simpler and safer than an
// incremental sync. Online: fetch fresh, cache it, return it. Offline: serve whatever
// was last cached (empty array if never cached).
@Injectable({ providedIn: 'root' })
export class MasterCacheService {
  private readonly dropdownService = inject(PopulateDropdownService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly locationService = inject(LocationSelectionService);

  // buyers/suppliers/products/loadingSites share their masterCache key with
  // ProductService/BusinessPartnerService/LoadingSiteService's offline-create tracking
  // (createOffline appends a placeholder there; SyncEngineService flags it _needsRename or
  // _offlineSyncFailed on the corresponding row). This refresh fires on every online-transition
  // (OfflinePrefetchService), so it must merge-preserve those rows instead of overwriting the
  // whole key — see cacheThroughMergeProtected.
  getBuyers(): Observable<any[]> {
    return this.cacheThroughMergeProtected('buyers', () => this.dropdownService.getBuyers());
  }

  getSuppliers(): Observable<any[]> {
    return this.cacheThroughMergeProtected('suppliers', () => this.dropdownService.getSuppliers());
  }

  getProducts(): Observable<any[]> {
    return this.cacheThroughMergeProtected('products', () => this.dropdownService.getProducts());
  }

  getLoadingSites(): Observable<any[]> {
    return this.cacheThroughMergeProtected('loadingSites', () => this.dropdownService.getLoadingSites());
  }

  getTransportModes(): Observable<any> {
    return this.cacheThrough('transportModes', () => this.dropdownService.getTransportMode());
  }

  getFumigationTypes(): Observable<any> {
    return this.cacheThrough('fumigationTypes', () => this.dropdownService.getFumigationType());
  }

  // Countries are a bounded, tenant-independent list (same shape as transportModes/
  // fumigationTypes) — full fetch-and-replace via getCountriesNEW() (the non-paginated
  // countrystatecity endpoint), not the query-driven GeoNames search UtilService.searchCountry()
  // uses elsewhere, which returns nothing useful to cache for a blank/offline query.
  getCountries(): Observable<any> {
    return this.cacheThrough('countries', () => this.locationService.getCountriesNEW());
  }

  private cacheThrough(key: string, fetch: () => Observable<any>): Observable<any> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return this.readCache(key);
    }
    return fetch().pipe(
      tap((data) => {
        if (isOfflineCapableNative()) {
          offlineDb.masterCache.put({ key: scopedKey(key), data, fetchedAt: Date.now() });
        }
      }),
      catchError((err) => (isOfflineCapableNative() ? this.readCache(key) : throwError(() => err))),
    );
  }

  // Same shape as cacheThrough, but never blindly overwrites `key` — any row already cached
  // there carrying _offlineClientUuid (offline-pending / needs-rename) is preserved and
  // re-appended on top of the fresh server data, instead of being silently wiped by this
  // dropdown refresh the moment the device reconnects.
  private cacheThroughMergeProtected(key: string, fetch: () => Observable<any[]>): Observable<any[]> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return this.readCache(key);
    }
    return fetch().pipe(
      switchMap((data) => (isOfflineCapableNative() ? from(this.mergeProtectedAndCache(key, data)) : of(data))),
      catchError((err) => (isOfflineCapableNative() ? this.readCache(key) : throwError(() => err))),
    );
  }

  private async mergeProtectedAndCache(key: string, freshData: any[]): Promise<any[]> {
    const cacheKey = scopedKey(key);
    const existingEntry = await offlineDb.masterCache.get(cacheKey);
    const existingList = Array.isArray(existingEntry?.data) ? (existingEntry!.data as any[]) : [];
    const protectedRows = existingList.filter(isProtected);
    const merged = [...protectedRows, ...(Array.isArray(freshData) ? freshData : [])];
    await offlineDb.masterCache.put({ key: cacheKey, data: merged, fetchedAt: Date.now() });
    return merged;
  }

  private readCache(key: string): Observable<any[]> {
    return from(offlineDb.masterCache.get(scopedKey(key))).pipe(
      switchMap((entry) => of((entry?.data as any[]) ?? [])),
    );
  }
}
